import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { btnPrimary } from "@/components/kit";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";

// Liens internes vers les pages commerciales, selon le sujet de l'article
const DEFAULT_LINKS = [
  { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle d'entreprise à Lyon" },
  { to: "/soiree-entreprise-lyon", label: "Organisation de soirée d'entreprise à Lyon" },
  { to: "/seminaire-convention-lyon", label: "Organisation de séminaire à Lyon" },
];
function relatedLinks(slug, title) {
  const t = `${slug} ${title}`.toLowerCase();
  if (t.includes("cse")) return [
    { to: "/evenements-cse", label: "Événements CSE : arbre de Noël, family day, soirées" },
    { to: "/evenements-cse/lyon", label: "Événements CSE à Lyon" },
    { to: "/evenements-cse/grenoble", label: "Événements CSE à Grenoble" },
    { to: "/evenements-cse/valence", label: "Événements CSE à Valence" },
  ];
  if (t.includes("family") || t.includes("journee-du-personnel") || t.includes("journée du personnel")) return [
    { to: "/evenements-cse", label: "Événements CSE : family day, journée du personnel" },
    { to: "/evenements-cse#family-day", label: "La formule Family Day en détail" },
    { to: "/evenements-cse/lyon", label: "Événements CSE à Lyon" },
    { to: "/devis", label: "Décrire votre journée du personnel" },
  ];
  if (t.includes("noel") || t.includes("noël")) return [
    { to: "/fete-de-noel-entreprise/lyon", label: "Fête de Noël d'entreprise à Lyon" },
    { to: "/soiree-entreprise-lyon", label: "Soirée d'entreprise à Lyon" },
    { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle à Lyon" },
  ];
  if (t.includes("salon")) return [
    { to: "/salon-professionnel-lyon", label: "Organisation de salon professionnel à Lyon" },
    { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle à Lyon" },
    { to: "/seminaire-convention-lyon", label: "Séminaire et convention à Lyon" },
  ];
  if (t.includes("inauguration") || t.includes("boutique")) return [
    { to: "/inauguration-entreprise-lyon", label: "Organisation d'inauguration à Lyon" },
    { to: "/inauguration-entreprise-paris", label: "Organisation d'inauguration à Paris" },
    { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle à Lyon" },
  ];
  if (t.includes("lancement")) return [
    { to: "/lancement-produit-lyon", label: "Lancement de produit à Lyon" },
    { to: "/lancement-produit-paris", label: "Lancement de produit à Paris" },
    { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle à Lyon" },
  ];
  if (t.includes("séminaire") || t.includes("seminaire") || t.includes("convention")) return [
    { to: "/seminaire-convention-lyon", label: "Séminaire et convention à Lyon" },
    { to: "/agence-evenementielle-clermont-ferrand", label: "Agence événementielle à Clermont-Ferrand" },
    { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle à Lyon" },
  ];
  if (t.includes("team")) return [
    { to: "/team-building-entreprise/lyon", label: "Team building d'entreprise à Lyon" },
    { to: "/agence-evenementielle-grenoble", label: "Agence événementielle à Grenoble" },
    { to: "/agence-evenementielle-entreprise-lyon", label: "Agence événementielle à Lyon" },
  ];
  return DEFAULT_LINKS;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-5 text-center">
        <p className="font-serif text-4xl text-ink">Article introuvable</p>
        <Link to="/blog" className={`${btnPrimary} mt-8`}>Retour au blog</Link>
      </div>
    );
  }
  if (!post) return <div className="flex min-h-screen items-center justify-center text-ink/40">Chargement…</div>;

  return (
    <>
      <Seo title={post.title} description={post.excerpt} path={`/blog/${slug}`} />
      <article>
        <section className="relative h-[60vh] min-h-[440px] overflow-hidden pt-20">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="relative z-10 mx-auto flex h-full max-w-4xl flex-col justify-end px-5 pb-14 md:px-10">
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">{post.category}</span>
            <h1 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-white md:text-6xl">{post.title}</h1>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-5 py-16 md:px-10 md:py-24">
          <Link to="/blog" className="mb-10 inline-flex items-center gap-2 text-sm text-ink/50 hover:text-lime-dim"><ArrowLeft size={15} /> Tous les articles</Link>
          <p className="mb-10 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-[0.15em] text-ink/45" data-testid="article-meta">
            <span>L'équipe Désir Events</span>
            {post.created_at && (
              <span>· <time dateTime={post.created_at}>{new Date(post.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</time></span>
            )}
          </p>
          <Reveal>
            <p className="mb-10 border-l-2 border-lime pl-6 font-serif text-2xl italic leading-snug text-ink/80">{post.excerpt}</p>
          </Reveal>
          <div className="space-y-6">
            {post.content.split("\n\n").map((para, i) => (<p key={i} className="text-lg leading-relaxed text-ink/75">{para}</p>))}
          </div>

          {/* Maillage interne vers les pages commerciales */}
          <nav className="mt-14 rounded-2xl border border-ink/10 bg-white p-7" data-testid="article-related" aria-label="Pages liées">
            <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40">Nos expertises liées</p>
            <ul className="mt-4 space-y-3">
              {relatedLinks(slug, post.title).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="group flex items-center justify-between text-sm font-medium text-ink hover:text-lime-dim">
                    {l.label}
                    <ArrowUpRight size={15} className="text-ink/30 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-lime-dim" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-16 rounded-2xl border border-ink/10 bg-mist p-8 text-center">
            <p className="font-serif text-3xl text-ink">Un projet en tête ?</p>
            <p className="mx-auto mt-3 max-w-md text-sm text-ink/55">Première réponse sous 24 h ouvrées, puis 3 premières pistes chiffrées.</p>
            <Link to="/devis" onClick={() => track("click_devis")} className={`${btnPrimary} mt-6`} data-testid="article-devis-btn">Recevoir mon devis <ArrowRight size={16} /></Link>
          </div>
        </div>
      </article>
    </>
  );
}
